import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { asItems, formatApiError, prefixUrl } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // WHY: the backend returns { items: [...] } but the older code did
    // `res.data?.posts || res.data || []`. When items was an empty array
    // that fallback yielded the outer object `{ items: [] }` and calling
    // `.map()` on it crashed the page. We now explicitly accept:
    //   1. a raw array,
    //   2. { posts: [...] },
    //   3. { items: [...] } (via asItems),
    // and default to [] on error. Safe against every shape the API can
    // return today.
    setLoading(true);
    setError("");
    api.get("/blog")
      .then((res) => {
        const data = res.data;
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.posts) ? data.posts : asItems(data));
        setPosts(rows.map((post) => ({ ...post, cover_image: prefixUrl(post.cover_image) })));
      })
      .catch((requestError) => {
        setPosts([]);
        setError(formatApiError(requestError) || "Blog posts could not be loaded.");
      })
      .finally(() => setLoading(false));
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8">Blog & Insights</h1>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-xl p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3" />
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center" role="alert">
        <h1 className="text-3xl font-bold mb-3">Blog could not be loaded</h1>
        <p className="text-muted-foreground">{error}</p>
        <Button type="button" className="mt-6" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="h-4 w-4 mr-2" />Try again</Button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-4">Blog & Insights</h1>
        <p className="text-muted-foreground">
          No blog posts yet. Check back soon for market insights, buying guides, and real estate tips!
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-2">Blog & Insights</h1>
      <p className="text-muted-foreground mb-8">
        Market trends, buying guides, and expert real estate advice.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, i) => (
          <article key={post._id || post.slug} style={{ animationDelay: `${Math.min(i, 9) * 60}ms`, animationFillMode: "both" }} className="premium-card premium-card-interactive group border rounded-2xl overflow-hidden hover:-translate-y-0.5 transition-all duration-300 animate-fade-up">
            {post.cover_image && (
              <div className="overflow-hidden">
                <img src={post.cover_image} alt={post.title} loading="lazy" decoding="async" width="480" height="160" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <div className="p-5">
              {post.category && (
                <Badge variant="secondary" className="mb-2">{post.category}</Badge>
              )}
              <h2 className="font-semibold text-lg mb-2 line-clamp-2">{post.title}</h2>
              <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                {post.excerpt || post.content?.substring(0, 150)}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  {post.author && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" /> {post.author}
                    </span>
                  )}
                  {post.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(post.created_at).toLocaleDateString("en-IN")}
                    </span>
                  )}
                </div>
                <Link
                  to={`/blog/${post.slug || post._id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  Read <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
